// Tâmplăria unui gol de fațadă, ca geometrie: toc cu adâncime, canate cu
// cercevea (sau baghetă, la cel fix), montanți, geam, șpaleți, mânere, prag —
// din ce scrie fațada pe gol (`architecture.opening.joinery`, v.
// `fatada.joinery_din_spec`), nu din convenții ale machetei. Separat de
// randor ca să poată fi PROBAT: unde stă mânerul și încotro dau normalele
// se văd într-un test, nu doar pe ecran.
//
// Reperul local al golului: x de-a lungul lui, de la `a` spre `b`; y pe
// normala care iese din clădire (`n`), cu 0 în planul zidăriei (linia
// conturului); z absolut. Finisajul fațadei are fața exterioară la
// y = `finisaj`; tocul stă cu `retragere_m` în spatele ei.
import * as THREE from "three";
import type { GolFatada } from "./sit3d";
import { fundalCamerei, samantaDin } from "./camera3d";

/** Implicitul machetei când documentul nu scrie o cotă — aceleași numere ca
 *  `fatada.TAMPLARIE_IMPLICITA`, ca un gol fără fațadă să arate ca unul cu. */
export const IMPLICIT = {
  profil: 0.07, adancime: 0.075, retragere: 0.10,
  cercevea: 0.065, treapta: 0.012, bagheta: 0.03, traversaUsa: 0.14,
  montant: 0.09, geam: 0.024,
  manerZ: 1.05, pragH: 0.02,
  /** golul din finisaj e cu atât mai mic decât fereastra pe șpaleți și la
   *  buiandrug (importul, macheta): finisajul vine PESTE toc; tocul începe
   *  cu atât înăuntrul golului */
  inset: 0.012,
  /** fundalul întunecat al încăperii, chiar în fața zidăriei */
  interiorY: 0.008,
  /** înălțimea camerei închipuite din spatele geamului (pardoseală → tavan) */
  camera_h: 2.75,
  /** cât iese finisajul din zidărie când nu se știe: 16,5 cm termosistem + 3 cm */
  finisaj: 0.195,
};

/** Cât stă căptușeala golului în spatele pereților găurii din finisaj: destul
 *  cât să nu se bată cu ei (peste 1 cm), puțin cât să nu se vadă ca treaptă. */
export const CAPTUSEALA_M = 0.015;

export interface PartiTamplarie {
  toc: THREE.BufferGeometry[];
  geam: THREE.BufferGeometry[];
  interior: THREE.BufferGeometry[];
  maner: THREE.BufferGeometry[];
  spalet: THREE.BufferGeometry[];
  prag: THREE.BufferGeometry[];
}

/** Un acumulator de triunghiuri în reperul local, vărsat ca o geometrie cu
 *  poziții și normale în lume (neindexată, ca să se poată uni cu restul). */
export class Trunchi {
  poz: number[] = [];
  nrm: number[] = [];
  constructor(private a: number[], private u: number[], private n: number[]) {}
  private P(x: number, y: number, z: number): number[] {
    return [this.a[0] + this.u[0] * x + this.n[0] * y, this.a[1] + this.u[1] * x + this.n[1] * y, z];
  }
  private N(x: number, y: number, z: number): number[] {
    return [this.u[0] * x + this.n[0] * y, this.u[1] * x + this.n[1] * y, z];
  }
  /** Patrulater p0→p1→p2→p3 (local), cu normala locală dată.
   *
   *  TRIUNGHIURILE SE ÎNFĂȘOARĂ DUPĂ NORMALA DIN LUME, nu după ordinea în care
   *  au fost scrise: reperul local (u, n, z) e drept la unele goluri și stâng
   *  la altele (după cum e scris golul de la `a` la `b`), deci aceeași ordine
   *  dădea fețe întoarse spre afară la unele și spre înăuntru la celelalte. Pe
   *  materialele cu o singură față (pragul, mânerul, fundalul, șpaletul) asta
   *  însemna că la o parte din goluri se desena INTERIORUL cutiei: pragul își
   *  arăta fața de jos, chiar în planul bordurii de sub el, și pâlpâia cu ea
   *  (67 px, 5.10.2026 — ieșit la iveală când glaful de sub ușă, care îl
   *  acoperea, a plecat), iar șpaletul și fundalul lipseau cu totul. */
  patrulater(p: number[][], nl: number[]): void {
    const w = p.map(([x, y, z]) => this.P(x, y, z));
    const nn = this.N(nl[0], nl[1], nl[2]);
    const e1 = [w[1][0] - w[0][0], w[1][1] - w[0][1], w[1][2] - w[0][2]];
    const e2 = [w[2][0] - w[0][0], w[2][1] - w[0][1], w[2][2] - w[0][2]];
    const g = [e1[1] * e2[2] - e1[2] * e2[1], e1[2] * e2[0] - e1[0] * e2[2], e1[0] * e2[1] - e1[1] * e2[0]];
    const spreAfara = g[0] * nn[0] + g[1] * nn[1] + g[2] * nn[2] >= 0;
    for (const i of spreAfara ? [0, 1, 2, 0, 2, 3] : [0, 2, 1, 0, 3, 2]) { this.poz.push(...w[i]); this.nrm.push(...nn); }
  }
  /** Cutie locală [x0,x1]×[y0,y1]×[z0,z1], cu normalele spre exterior. */
  cutie(x0: number, x1: number, y0: number, y1: number, z0: number, z1: number): void {
    if (x1 - x0 <= 0 || y1 - y0 <= 0 || z1 - z0 <= 0) return;
    this.patrulater([[x0, y0, z0], [x0, y0, z1], [x1, y0, z1], [x1, y0, z0]], [0, -1, 0]);
    this.patrulater([[x0, y1, z0], [x1, y1, z0], [x1, y1, z1], [x0, y1, z1]], [0, 1, 0]);
    this.patrulater([[x0, y0, z0], [x0, y1, z0], [x0, y1, z1], [x0, y0, z1]], [-1, 0, 0]);
    this.patrulater([[x1, y0, z0], [x1, y0, z1], [x1, y1, z1], [x1, y1, z0]], [1, 0, 0]);
    this.patrulater([[x0, y0, z0], [x1, y0, z0], [x1, y1, z0], [x0, y1, z0]], [0, 0, -1]);
    this.patrulater([[x0, y0, z1], [x0, y1, z1], [x1, y1, z1], [x1, y0, z1]], [0, 0, 1]);
  }
  /** Ramă dreptunghiulară din patru cutii: lățime `lat` (jos `latJos`). */
  rama(x0: number, x1: number, z0: number, z1: number, lat: number, y0: number, y1: number,
       latJos = lat): void {
    this.cutie(x0, x1, y0, y1, z0, z0 + latJos);
    this.cutie(x0, x1, y0, y1, z1 - lat, z1);
    this.cutie(x0, x0 + lat, y0, y1, z0 + latJos, z1 - lat);
    this.cutie(x1 - lat, x1, y0, y1, z0 + latJos, z1 - lat);
  }
  geometrie(): THREE.BufferGeometry | null {
    if (!this.poz.length) return null;
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(this.poz, 3));
    g.setAttribute("normal", new THREE.Float32BufferAttribute(this.nrm, 3));
    return g;
  }
}

/** Canatele golului, de la `a` spre `b`, cu felul fiecăruia. La ușă foaia
 *  stă lângă tocul balamalei (`balama`), panoul fix de partea cealaltă. */
export function canatele(gol: GolFatada): string[] {
  const t = gol.tamplarie;
  let moduri: string[];
  if (t?.canate?.length) moduri = [...t.canate];
  else {
    const lat = Math.hypot(gol.b[0] - gol.a[0], gol.b[1] - gol.a[1]);
    const n = gol.canate ?? (gol.tip === "Door" ? (lat > 1.0 ? 2 : 1) : (lat > 1.5 ? 2 : 1));
    moduri = gol.tip === "Door"
      ? [...Array.from({ length: n - 1 }, () => "fixed"), "casement"]
      : Array.from({ length: n }, () => "tilt_turn");
  }
  if (gol.tip === "Door" && gol.balama === "start" && moduri.length > 1
      && moduri[moduri.length - 1] !== "fixed" && moduri[0] === "fixed") {
    moduri.reverse();
  }
  return moduri;
}

/** Lățimile canatelor, în aceeași ordine ca `canatele` (întoarse odată cu
 *  ele), scalate ca să umple exact `liber`; egale când golul nu le scrie. */
export function latimile(gol: GolFatada, moduri: string[], liber: number): number[] {
  const n = moduri.length;
  const scrise = gol.tamplarie?.latimi;
  let w = scrise && scrise.length === n ? [...scrise] : Array.from({ length: n }, () => 1);
  const brute = gol.tamplarie?.canate;
  if (scrise && brute && brute.length === n && brute[0] !== moduri[0]) w.reverse();
  const suma = w.reduce((s, v) => s + v, 0) || 1;
  w = w.map((v) => (v / suma) * liber);
  return w;
}

/** Geometria tâmplăriei golului între cotele absolute `z0`..`z1`, cu
 *  finisajul fațadei ieșit `finisaj` din zidărie. */
export function geometriaGolului(gol: GolFatada, z0: number, z1: number,
                                 finisaj = IMPLICIT.finisaj): PartiTamplarie {
  const out: PartiTamplarie = { toc: [], geam: [], interior: [], maner: [], spalet: [], prag: [] };
  // GEAMUL PE COLȚ: reperul se întoarce așa încât colțul să cadă la `b`
  // (canatele și lățimile lor, odată cu el)
  if (gol.colt?.la === "a" && gol.tip !== "Door") {
    const t0 = gol.tamplarie;
    gol = { ...gol, a: gol.b, b: gol.a, colt: { ...gol.colt, la: "b" },
            lipitA: gol.lipitB, lipitB: gol.lipitA,
            ...(t0 ? { tamplarie: { ...t0, canate: [...t0.canate].reverse(),
                                    ...(t0.latimi ? { latimi: [...t0.latimi].reverse() } : {}) } } : {}) };
  }
  const W = Math.hypot(gol.b[0] - gol.a[0], gol.b[1] - gol.a[1]);
  const H = z1 - z0;
  if (W < 0.2 || H < 0.2) return out;
  const u = [(gol.b[0] - gol.a[0]) / W, (gol.b[1] - gol.a[1]) / W];
  const n = gol.n;
  const t = gol.tamplarie;
  const usa = gol.tip === "Door";
  const profil = t?.profil_m ?? gol.profil ?? IMPLICIT.profil;
  const adancime = t?.adancime_m ?? IMPLICIT.adancime;
  const retragere = t?.retragere_m ?? IMPLICIT.retragere;
  const cercevea = t?.cercevea_m ?? IMPLICIT.cercevea;
  const treapta = t?.treapta_m ?? IMPLICIT.treapta;
  const bagheta = t?.bagheta_m ?? IMPLICIT.bagheta;
  const traversaUsa = t?.traversa_usa_m ?? IMPLICIT.traversaUsa;
  const montant = t?.montant_m ?? IMPLICIT.montant;
  const geam = t?.geam_m ?? IMPLICIT.geam;
  const maner = t ? t.maner : { tip: "lever", inaltime_m: IMPLICIT.manerZ };
  const prag = usa ? (t ? t.prag : { inaltime_m: IMPLICIT.pragH }) : undefined;
  // DE UNDE SE MĂSOARĂ MÂNERUL ȘI INTERFONUL: de la suprafața pe care stă omul.
  // La o fereastră sau o ușă de balcon e pardoseala (golul începe deasupra ei,
  // `parapet` ≥ 0); la ușa de bloc coborâtă la teren golul începe SUB pardoseala
  // parterului (`parapet` < 0, soclul) și omul stă pe teren, la prag — măsurat
  // de la pardoseală, bara ieșea cu soclul (0,90) prea sus (Pipera, 3.11.2026).
  const stat = Math.min(z0, z0 - gol.parapet);

  // planurile pe adâncime: fundalul, tocul (între yb și yf), fața finisajului
  const yInt = IMPLICIT.interiorY;
  const yf = Math.max(finisaj - retragere, yInt + 0.01 + adancime);
  const yb = Math.max(yInt + 0.006, yf - adancime);
  const yOut = Math.max(finisaj, yf);
  const e = IMPLICIT.inset;
  const x0 = e, x1 = W - e, zJ = usa ? z0 : z0 + e, zS = z1 - e;

  // FRONT VITRAT CONTINUU: pe partea cu golul vecin lipit (`lipitA`/`lipitB`)
  // nu e stâlp de toc și nici șpalet — geamul merge până la margine, iar
  // între cele două goluri stă un singur montant, desenat de golul din stânga
  const lipA = gol.lipitA !== undefined, lipB = gol.lipitB !== undefined;
  // GEAMUL PE COLȚ, la `b`: fără stâlp, fără șpalet; tocul (traversele de sus
  // și de jos) merge până la MONTANTUL DE COLȚ, care stă pe linia feței vecine
  // (`colt.x`) cât adâncimea tocului — desenat de unul singur din pereche
  // (`stapan`); traversele celuilalt se opresc în fața lui. Geamul ajunge la
  // montant, fundalul până la linia vecinului.
  const colt = gol.colt && gol.tip !== "Door" ? gol.colt : undefined;
  // ȘPALEȚII: căptușeala golului, de la fundal până la fața finisajului, cu
  // `CAPTUSEALA_M` ÎN SPATELE pereților găurii din finisaj (gaura e cu `inset`
  // mai mică decât golul — finisajul vine peste toc, v. `RandareSit3D`). În
  // termosistem stă ascunsă în cutia finisajului; pe fațada ventilată închide
  // golul de aer dintre substrat și plăci, prin care s-ar vedea în spatele
  // plăcilor. Pusă chiar în planul pereților găurii, s-ar bate cu ei. Jos nu
  // mai e nimic: pervazul urcă până sub toc (12.10.2026).
  const sp = new Trunchi(gol.a, u, n);
  const cx0 = x0 + CAPTUSEALA_M, cx1 = x1 - CAPTUSEALA_M, czS = zS - CAPTUSEALA_M;
  if (!lipA) sp.patrulater([[cx0, yInt, zJ], [cx0, yOut, zJ], [cx0, yOut, czS], [cx0, yInt, czS]], [1, 0, 0]);
  if (!lipB && !colt) sp.patrulater([[cx1, yInt, zJ], [cx1, yInt, czS], [cx1, yOut, czS], [cx1, yOut, zJ]], [-1, 0, 0]);
  // la colț căptușeala buiandrugului se taie în unghi, pe bisectoare, cu a vecinului
  if (colt) sp.patrulater([[cx0, yInt, czS], [cx0, yOut, czS], [colt.x + yOut, yOut, czS], [colt.x + yInt, yInt, czS]], [0, 0, -1]);
  else sp.patrulater([[cx0, yInt, czS], [cx0, yOut, czS], [cx1, yOut, czS], [cx1, yInt, czS]], [0, 0, -1]);
  // FUNDALUL încăperii, în fața zidăriei: CAMERA închipuită din spatele
  // geamului (v. `camera3d`), cu pardoseala la parapetul golului sub el
  const xf = colt ? colt.x : x1;
  const pod = z0 - (gol.parapet ?? 0);
  const cx = gol.a[0] + u[0] * (x0 + xf) / 2, cy = gol.a[1] + u[1] * (x0 + xf) / 2;
  out.interior.push(fundalCamerei(gol.a, u, n, yInt, x0, xf, zJ, zS, pod, {
    x0: x0 - 1.4, x1: xf + 1.4, adancime: 4.2, inaltime: IMPLICIT.camera_h,
    gol0: x0, gol1: xf, samanta: samantaDin(cx, cy, pod),
  }));

  // TOCUL: ramă (la ușă fără traversă jos: pragul) …
  // …TRAS CU 1,5 mm DE PE ȘPALEȚI. Fețele lui de afară (x0, x1, zS) stăteau
  // chiar în planul șpaleților: două fețe coplanare, una închisă și una
  // deschisă, care de departe se băteau pe adâncime — liniile punctate de
  // lângă ferestre din imaginea de ansamblu («pâlpâie», Cătălin, 1.10.2026;
  // măsurat pe macheta redusă la 800 px: 17 coloane cu toc/perete alternând
  // rând de rând, a doua lovitură la 0,000 m în spatele primei).
  const toc = new Trunchi(gol.a, u, n);
  const eps = 0.0015;
  // marginile tocului: la marginea lipită, fără stâlp — geamul ajunge la ea
  const tx0 = lipA ? x0 : x0 + eps;
  const tx1 = colt ? colt.x + (colt.stapan ? yf : yb) : (lipB ? x1 : x1 - eps);
  let izJ: number;
  if (usa) {
    toc.cutie(tx0, tx1, yb, yf, zS - profil, zS - eps);
    if (!lipA) toc.cutie(x0 + eps, x0 + profil, yb, yf, zJ, zS - profil);
    if (!lipB) toc.cutie(x1 - profil, x1 - eps, yb, yf, zJ, zS - profil);
    izJ = zJ + (prag?.inaltime_m ?? 0);
  } else {
    toc.cutie(tx0, tx1, yb, yf, zJ + eps, zJ + profil);
    toc.cutie(tx0, tx1, yb, yf, zS - profil, zS - eps);
    if (!lipA) toc.cutie(x0 + eps, x0 + profil, yb, yf, zJ + profil, zS - profil);
    if (!lipB && !colt) toc.cutie(x1 - profil, x1 - eps, yb, yf, zJ + profil, zS - profil);
    izJ = zJ + profil;
  }
  // MONTANTUL DE COLȚ, între traverse, pe linia feței vecine
  if (colt?.stapan) toc.cutie(colt.x + yb, colt.x + yf, yb, yf, izJ, zS - profil);
  // MONTANTUL dintre două goluri lipite: unul singur, pe rost, până la fața
  // finisajului (acoperă și fâșia de finisaj dintre ele)
  if (lipB) {
    const gap = gol.lipitB as number;
    const lat = Math.max(montant, gap);
    const c = x1 + gap / 2;
    toc.cutie(c - lat / 2, c + lat / 2, yb, Math.max(yf, yOut) + 0.001, zJ, zS);
  }
  const ix0 = lipA ? x0 : x0 + profil;
  const ix1 = colt ? colt.x + yb : (lipB ? x1 : x1 - profil);
  const izS = zS - profil;
  // … CANATELE, cu montanți între ele
  const moduri = canatele(gol);
  const nc = moduri.length;
  // SUPRALUMINA (ușa de intrare în bloc): canatele se opresc sub traversa ei,
  // iar deasupra, pe fiecare coloană, un geam fix cu baghetă
  const supra = usa && (t?.supralumina_m ?? 0) > 0.1 ? Math.min(t!.supralumina_m!, (izS - izJ) / 2) : 0;
  const izL = supra ? izS - supra : izS;
  // INTERFONUL: pe canatul fix al cărui index îl scrie fațada (în ordinea
  // documentului — întors odată cu canatele, ca lățimile)
  const icScris = t?.interfon && t.interfon.canat !== null ? t.interfon.canat : null;
  const icCanat = icScris === null ? null
    : (t?.canate && t.canate.length === nc && t.canate[0] !== moduri[0] ? nc - 1 - icScris : icScris);
  const coloane: number[][] = [];
  // CANATE PE LĂȚIMILE LOR (ușa: panourile fixe + foaia de 0,90), nu egale
  const latimi = latimile(gol, moduri, ix1 - ix0 - montant * (nc - 1));
  const lw = Math.min(...latimi);
  const yc1 = yf - treapta, yc0 = yb - 0.01;
  const yg0 = yf - 0.038, yg1 = yg0 + geam;
  const ov = 0.008;
  const gm = new Trunchi(gol.a, u, n);
  const mn = new Trunchi(gol.a, u, n);
  if (lw > 0.1) {
    let a = ix0;
    for (let i = 0; i < nc; i++) {
      if (i) a += montant;
      const b = a + latimi[i];
      if (i) toc.cutie(a - montant, a, yb, yf, izJ, izS);
      const mobil = moduri[i] !== "fixed";
      const lat = mobil ? cercevea : bagheta;
      const latJos = usa && mobil ? traversaUsa : lat;
      // la marginea lipită canatul n-are stâlp: geamul ajunge la montant
      const latS = (i === 0 && lipA) ? 0 : lat;
      const latD = (i === nc - 1 && lipB) ? 0 : lat;
      const ya = mobil ? yc0 : yf - 0.03;
      coloane.push([a, b]);
      toc.cutie(a - ov, b + ov, ya, yc1, izJ - ov, izJ - ov + latJos);
      toc.cutie(a - ov, b + ov, ya, yc1, izL + ov - lat, izL + ov);
      if (latS) toc.cutie(a - ov, a - ov + latS, ya, yc1, izJ - ov + latJos, izL + ov - lat);
      if (latD) toc.cutie(b + ov - latD, b + ov, ya, yc1, izJ - ov + latJos, izL + ov - lat);
      const gz0 = izJ + latJos - 0.01, gz1 = izL - lat + 0.01;
      if (icCanat === i && t?.interfon) {
        // BANDA INTERFONULUI: plină, cât canatul, în planul tocului, cu geam
        // sub și peste ea; aparatul (placa cu butoane) iese 1 cm din ea
        const zi0 = Math.max(gz0 + 0.1, stat + t.interfon.z_m);
        const zi1 = Math.min(gz1 - 0.1, zi0 + t.interfon.inaltime_m);
        gm.cutie(a + latS - 0.01, b - latD + 0.01, yg0, yg1, gz0, zi0);
        gm.cutie(a + latS - 0.01, b - latD + 0.01, yg0, yg1, zi1, gz1);
        toc.cutie(a + latS - 0.01, b - latD + 0.01, yf - 0.03, yc1, zi0, zi1);
        const xm = (a + b) / 2, lp = Math.min(0.16, (b - a - 2 * lat) * 0.7);
        mn.cutie(xm - lp / 2, xm + lp / 2, yc1, yc1 + 0.012, zi0 + 0.05, zi1 - 0.05);
      } else {
        gm.cutie(a + latS - 0.01, b - latD + 0.01, yg0, yg1, gz0, gz1);
      }
      if (mobil && maner && maner.tip === "bar") {
        // BARA DE TRAS a ușii de intrare: verticală, pe stâlpul dinspre
        // deschidere, pe AMBELE fețe ale foii, pe doi suporți
        const L = maner.lungime_m ?? 1.2;
        const zc = Math.max(z0 + 0.3 + L / 2, stat + maner.inaltime_m);
        const laStanga = nc === 1 ? gol.balama === "end" : !(i === 0 && nc > 1) && i === nc - 1;
        const xm = laStanga ? a + lat + 0.05 : b - lat - 0.05;
        for (const [yz, sens] of [[yc1, 1], [yc0, -1]] as const) {
          const yBara = yz + sens * 0.065;
          mn.cutie(xm - 0.016, xm + 0.016, Math.min(yBara, yBara + sens * 0.032), Math.max(yBara, yBara + sens * 0.032),
                   zc - L / 2, zc + L / 2);
          for (const zs of [zc - L / 2 + 0.12, zc + L / 2 - 0.12]) {
            mn.cutie(xm - 0.01, xm + 0.01, Math.min(yz, yBara), Math.max(yz, yBara), zs - 0.01, zs + 0.01);
          }
        }
      } else if (mobil && maner) {
        // MÂNERUL pe stâlpul dinspre mijloc (balamaua e la tocul de margine);
        // la un singur canat, pe stâlpul opus balamalei; la mijlocul canatului
        // sau la înălțimea scrisă, la canatele înalte
        const zm = H <= 1.6 && !usa ? (z0 + z1) / 2 : Math.max(z0 + 0.35, stat + maner.inaltime_m);
        let laStanga = i > 0 && i === nc - 1;
        if (nc === 1) laStanga = usa && gol.balama === "end";
        if (nc > 1 && i === 0) laStanga = false;
        const xm = laStanga ? a + lat / 2 + ov : b - lat / 2 - ov;
        const yi = yc0;
        mn.cutie(xm - 0.017, xm + 0.017, yi - 0.014, yi, zm - 0.04, zm + 0.04);
        mn.cutie(xm - 0.009, xm + 0.009, yi - 0.034, yi - 0.014, zm - 0.009, zm + 0.009);
        mn.cutie(xm - 0.008, xm + 0.008, yi - 0.05, yi - 0.034, zm - 0.12, zm + 0.012);
      }
      a = b;
    }
    if (supra) {
      // traversa supraluminii și geamul fix al fiecărei coloane, cu baghetă;
      // montanții dintre canate urcă până la toc, iar traversa se pune ÎNTRE
      // ei, pe coloane — dusă peste toată lățimea, fața ei din față stătea în
      // planul montantului, pe pătratul de încrucișare
      for (const [ca, cb] of coloane) {
        toc.cutie(ca, cb, yb, yf, izL, izL + montant);
        toc.rama(ca, cb, izL + montant, izS, bagheta, yf - 0.03, yc1);
        gm.cutie(ca + bagheta - 0.01, cb - bagheta + 0.01, yg0, yg1, izL + montant + bagheta - 0.01, izS - bagheta + 0.01);
      }
    }
  } else {
    gm.cutie(ix0, ix1, yg0, yg1, izJ, izS);
  }
  // PRAGUL ușii: de sub toc până cu 1,5 cm ÎNAINTEA feței finisajului — el
  // stă PE pervazul golului (glaful, un relief al fațadei care iese dincolo
  // de față), nu dincolo de el: ieșit cu 3 cm, fața lui din față stătea la
  // 1 cm de a pervazului, două profile unul sub altul (6.10.2026)
  if (usa && prag && prag.inaltime_m > 0) {
    const pr = new Trunchi(gol.a, u, n);
    pr.cutie(x0 + 0.001, x1 - 0.001, yb - 0.02, yOut - 0.015, zJ, zJ + prag.inaltime_m);
    const g = pr.geometrie(); if (g) out.prag.push(g);
  }
  for (const [k, tr] of [["toc", toc], ["geam", gm], ["maner", mn], ["spalet", sp]] as const) {
    const g = tr.geometrie();
    if (g) out[k].push(g);
  }
  return out;
}
